import type { ListItemProps } from "@/components/ListItem/ListItem";
import { ACCEPTABLE_PROGRESS_TERMINATION_REASON } from "@/features/person/utils/pgm-levels";
import { calculateTimeRemaining, toFormat } from "@/lib/dateHelpers";
import { ProgramStatus } from "@/types";
import type { ApplicationStage, ApplicationType } from "../types";
import type {
  ApplicationEmploymentDetails,
  EligibilityCheck,
  MembershipApplicationDetail,
  MembershipApplicationReviewer
} from "./queries";

export const APPLICATION_CRIMINAL_HISTORY_SCROLL_TARGET = "criminal-history-documents-section";
export const APPLICATION_EDUCATION_SCROLL_TARGET = "education-documents-section";
export const APPLICATION_EMPLOYMENT_SCROLL_TARGET = "employment-history-section";

export function hasEligibility(statuses: EligibilityCheck[] | undefined, type: string, status: string): boolean {
  return statuses?.some((e) => e.type === type && e.status === status) ?? false;
}

export function requiresEducationDocumentationReview(applicationType?: ApplicationType | string | null): boolean {
  return applicationType === "associate" || applicationType === "affiliate";
}

export type ApplicationTodoId = "criminal-history" | "education" | "employment" | "change-request";

export interface ApplicationTodoCheck {
  id: ApplicationTodoId;
  title: string;
  description: string;
  completed: boolean;
}

type ApplicationTodoInput = Pick<
  MembershipApplicationDetail,
  "applicationType" | "hasCriminalHistory" | "eligibilityStatuses"
> & {
  currentEmploymentDetails?: ApplicationEmploymentDetails | null;
  previousEmploymentDetails?: ApplicationEmploymentDetails | null;
};

export function hasCompleteAssociateEmploymentClassifications(
  currentEmploymentDetails: ApplicationEmploymentDetails | null | undefined,
  previousEmploymentDetails: ApplicationEmploymentDetails | null | undefined
): boolean {
  const employmentRows = [currentEmploymentDetails, previousEmploymentDetails].filter(
    (employment): employment is ApplicationEmploymentDetails => employment != null
  );

  return employmentRows.every((employment) => !!employment.jobClassification);
}
type ApplicationApprovalInput = ApplicationTodoInput &
  Pick<
    MembershipApplicationDetail,
    "applicationType" | "experienceCredits" | "memberApplicationEligibility" | "status"
  > & {
    reviewer: MembershipApplicationReviewer | null;
  };

export function getApplicationTodoChecks(application: ApplicationTodoInput): ApplicationTodoCheck[] {
  const checks: ApplicationTodoCheck[] = [];

  if (requiresEducationDocumentationReview(application.applicationType)) {
    checks.push({
      id: "education",
      title: "Review education documentation",
      description: "Confirm applicant has provided proof of highest level of education",
      completed: hasEligibility(application.eligibilityStatuses, "EDUCATION", "ELIGIBLE")
    });
  }

  if (application.applicationType === "associate") {
    checks.push({
      id: "employment",
      title: "Review employment history",
      description: "Confirm applicant employment history meets eligibility requirements",
      completed:
        hasEligibility(application.eligibilityStatuses, "EMPLOYMENT", "ELIGIBLE") &&
        hasCompleteAssociateEmploymentClassifications(
          application.currentEmploymentDetails,
          application.previousEmploymentDetails
        )
    });
  }

  if (application.hasCriminalHistory) {
    checks.push({
      id: "criminal-history",
      title: "Review criminal history",
      description:
        "Confirm applicant has provided documentation from a prior felony or misdemeanor conviction that does not preclude them from membership",
      completed: hasEligibility(application.eligibilityStatuses, "CRIMINAL_HISTORY", "ELIGIBLE")
    });
  }

  if (application.applicationType === "member") {
    const hasOpenChangeRequest = application.eligibilityStatuses.some((e) => e.type === "OPEN_CHANGE_REQUEST");
    if (hasOpenChangeRequest) {
      checks.push({
        id: "change-request",
        title: "Open change request has been approved",
        description: "Section needs to approve applicant's open change request",
        completed: hasEligibility(application.eligibilityStatuses, "OPEN_CHANGE_REQUEST", "ELIGIBLE")
      });
    }
  }

  return checks;
}

export function areAllApplicationTodosComplete(application: ApplicationTodoInput): boolean {
  return getApplicationTodoChecks(application).every((check) => check.completed);
}

export function areApplicationExperienceCreditsComplete(
  application: Pick<
    MembershipApplicationDetail,
    "applicationType" | "experienceCredits" | "memberApplicationEligibility"
  >
): boolean {
  if (application.applicationType !== "member") return true;

  if (application.memberApplicationEligibility) {
    return application.memberApplicationEligibility.experienceCredits.complete === true;
  }

  return application.experienceCredits?.complete === true;
}

export function canDenyApplication(application: Pick<MembershipApplicationDetail, "reviewer" | "status">): boolean {
  return !!(application.reviewer && isApplicationReviewableStatus(application.status));
}

export function isDraftApplicationStatus(status: ApplicationStage | string): boolean {
  return status === "draft";
}

export function isApplicationReviewableStatus(status: ApplicationStage | string): boolean {
  return status === "submitted" || status === "under_review";
}

/**
 * Application detail fields and eligibility review actions are editable only while
 * the application is submitted or under review. Approved/denied applications are read-only.
 */
export function canEditApplicationFields(application: Pick<MembershipApplicationDetail, "status">): boolean {
  return isApplicationReviewableStatus(application.status);
}

export function canEditApplicationEducationDetails(application: Pick<MembershipApplicationDetail, "status">): boolean {
  return canEditApplicationFields(application);
}

export function canApproveApplication(application: ApplicationApprovalInput): boolean {
  return !!(
    application.reviewer &&
    isApplicationReviewableStatus(application.status) &&
    areAllApplicationTodosComplete(application) &&
    areApplicationExperienceCreditsComplete(application)
  );
}

export const ACCEPTABLE_PROGRESS_END_DATE_LABEL = "Acceptable Progress End Date";
export const ACCEPTABLE_PROGRESS_TIME_LEFT_LABEL = "Time Left";

type AssociateReapplyApplicationInput = Pick<MembershipApplicationDetail, "applicationType"> & {
  person?: {
    latestProgram?: {
      status?: string | null;
      band?: {
        terminatedReason?: string | null;
      } | null;
    } | null;
  } | null;
};

export function isAssociateReapplyApplication(application: AssociateReapplyApplicationInput): boolean {
  if (application.applicationType !== "associate") return false;
  if (application.person?.latestProgram?.status !== ProgramStatus.TERMINATED) return false;
  return application.person.latestProgram.band?.terminatedReason === ACCEPTABLE_PROGRESS_TERMINATION_REASON;
}

export function buildAcceptableProgressOverviewOptions(expirationDate: string | null | undefined): ListItemProps[] {
  if (!expirationDate) return [];

  return [
    {
      iconName: "event",
      overline: ACCEPTABLE_PROGRESS_END_DATE_LABEL,
      headline: toFormat(expirationDate) ?? "--"
    },
    {
      iconName: "schedule",
      overline: ACCEPTABLE_PROGRESS_TIME_LEFT_LABEL,
      headline: calculateTimeRemaining(expirationDate) ?? "--"
    }
  ];
}
